import type {
  PersonaOpportunitySetDetail,
  PersonaOpportunitySetListPage,
  PersonaOpportunitySetRow,
} from "../api/persona-opportunity-sets.api";
import type { CreatePersonaSetFormValues } from "../types/persona-opportunities.types";

export const PENDING_PERSONA_SET_ID_PREFIX = "__pending__";

export function isPendingPersonaSetId(id: string): boolean {
  return id.startsWith(PENDING_PERSONA_SET_ID_PREFIX);
}

export function createPendingPersonaSetId(): string {
  return `${PENDING_PERSONA_SET_ID_PREFIX}${crypto.randomUUID()}`;
}

/** Optimistic list row shown while the worker has not yet returned a real set id. */
export function createOptimisticGeneratingSetRow(args: {
  id: string;
  tenantId: string;
  name: string;
  industry: string | null;
  industryCompetitors: string[];
  adPlatform: string | null;
  startDate?: string | null;
  endDate?: string | null;
}): PersonaOpportunitySetRow {
  const now = new Date().toISOString();
  return {
    id: args.id,
    tenantId: args.tenantId,
    name: args.name,
    status: "GENERATING",
    customerPartition: null,
    startDate: args.startDate ?? null,
    endDate: args.endDate ?? null,
    config: {
      industry: args.industry ?? "",
      industryCompetitors: args.industryCompetitors,
      adPlatforms: args.adPlatform ? [args.adPlatform] : [],
    },
    deletedAt: null,
    createdAt: now,
    updatedAt: now,
    opportunityCount: 0,
    scriptCount: 0,
  };
}

export function createOptimisticPersonaSetRow(
  values: CreatePersonaSetFormValues,
  tenantId: string,
  placeholderId: string,
): PersonaOpportunitySetRow {
  return createOptimisticGeneratingSetRow({
    id: placeholderId,
    tenantId,
    name: values.name,
    industry: values.industry,
    industryCompetitors: values.industryCompetitors,
    adPlatform: values.adPlatform,
    startDate: values.startDate,
    endDate: values.endDate,
  });
}

export function applyOptimisticCreateToList(
  prev: PersonaOpportunitySetListPage | undefined,
  row: PersonaOpportunitySetRow,
): PersonaOpportunitySetListPage {
  const items = (prev?.items ?? []).filter((set) => set.id !== row.id);
  return {
    items: [row, ...items],
    nextCursor: prev?.nextCursor ?? null,
    hasMore: prev?.hasMore ?? false,
  };
}

export function replacePersonaSetIdInList(
  prev: PersonaOpportunitySetListPage | undefined,
  placeholderId: string,
  realId: string,
): PersonaOpportunitySetListPage | undefined {
  if (!prev) return prev;
  if (prev.items.some((set) => set.id === realId)) {
    return {
      ...prev,
      items: prev.items.filter((set) => set.id !== placeholderId),
    };
  }
  return {
    ...prev,
    items: prev.items.map((set) => (set.id === placeholderId ? { ...set, id: realId } : set)),
  };
}

export function removePersonaSetFromList(
  prev: PersonaOpportunitySetListPage | undefined,
  setId: string,
): PersonaOpportunitySetListPage | undefined {
  if (!prev) return prev;
  const items = prev.items.filter((set) => set.id !== setId);
  if (items.length === prev.items.length) return prev;
  return { ...prev, items };
}

/**
 * Flip a FAILED/READY list row to GENERATING so the card loading UI appears
 * immediately on retry/regenerate — the list only polls every 2s otherwise.
 */
export function markPersonaSetGeneratingInList(
  prev: PersonaOpportunitySetListPage | undefined,
  setId: string,
): PersonaOpportunitySetListPage | undefined {
  if (!prev) return prev;
  let changed = false;
  const items = prev.items.map((set) => {
    if (set.id !== setId || set.status === "GENERATING") return set;
    changed = true;
    return { ...set, status: "GENERATING" as const, opportunityCount: 0, scriptCount: 0 };
  });
  return changed ? { ...prev, items } : prev;
}

/**
 * Same optimistic flip for the workspace detail cache. Selected set prefers
 * detail over the list row, so retry-from-workspace needs both.
 */
export function markPersonaSetGeneratingInDetail(
  prev: PersonaOpportunitySetDetail | undefined,
  setId: string,
): PersonaOpportunitySetDetail | undefined {
  if (!prev || prev.set.id !== setId) return prev;
  if (prev.set.status === "GENERATING" && prev.personas.length === 0) return prev;
  return {
    ...prev,
    set: { ...prev.set, status: "GENERATING", opportunityCount: 0, scriptCount: 0 },
    personas: [],
  };
}

export type PersonaSetStatus = PersonaOpportunitySetRow["status"];

export interface PersonaSetStatusTransition {
  setId: string;
  name: string;
  to: Extract<PersonaSetStatus, "READY" | "FAILED">;
}

export function buildPersonaSetStatusMap(
  sets: readonly { id: string; status: PersonaSetStatus }[],
): Map<string, PersonaSetStatus> {
  return new Map(sets.map((set) => [set.id, set.status]));
}

/**
 * Generation outcomes are read off the polled list rather than reported by the
 * request that started them: the 202 says nothing about how the run ends, and a
 * run can also finish while the user is on another page or after a reload.
 *
 * Only sets seen GENERATING in the previous snapshot count, so a first load that
 * already contains finished sets stays silent.
 */
export function detectPersonaSetGenerationTransitions(
  previous: Map<string, PersonaSetStatus>,
  sets: readonly { id: string; name: string; status: PersonaSetStatus }[],
): PersonaSetStatusTransition[] {
  const transitions: PersonaSetStatusTransition[] = [];
  for (const set of sets) {
    if (previous.get(set.id) !== "GENERATING") continue;
    if (set.status !== "READY" && set.status !== "FAILED") continue;
    transitions.push({ setId: set.id, name: set.name, to: set.status });
  }
  return transitions;
}

/** Flip a list row to READY once generation completes. */
export function markPersonaSetReadyInList(
  prev: PersonaOpportunitySetListPage | undefined,
  setId: string,
): PersonaOpportunitySetListPage | undefined {
  if (!prev) return prev;
  let changed = false;
  const items = prev.items.map((set) => {
    if (set.id !== setId || set.status === "READY") return set;
    changed = true;
    return { ...set, status: "READY" as const };
  });
  return changed ? { ...prev, items } : prev;
}
